import { Checkbox, CloseIcon } from '@bp/ui-components';
import classNames from 'classnames';
import dayjs from 'dayjs';
import { CSSProperties, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './TimeGridMigrationMapping.module.scss';
import { NO_TARGET, TimeGridMigrationState } from './useTimeGridMigration';

type TimeGridMigrationMappingProps = {
  migration: TimeGridMigrationState;
  newTimeGridName: string | null;
  oldTimeGridName: string | null;
  resetAvailabilitiesLabel: string;
};

// Feste Zeilenhöhe: so lassen sich die Verbindungslinien aus der Position in der Liste berechnen,
// ohne das DOM auszumessen. Die Werte gehen als CSS-Variablen an das Stylesheet.
const ROW_HEIGHT = 48;
const ROW_GAP = 8;
const rowCenter = (index: number) => index * (ROW_HEIGHT + ROW_GAP) + ROW_HEIGHT / 2;

const formatTime = (entry: { start: string; end: string }) =>
  `${dayjs(entry.start).format('HH:mm')}–${dayjs(entry.end).format('HH:mm')}`;

/**
 * Links alle Stunden des bisherigen, rechts alle des neuen Zeitrasters; Linien zeigen die Zuordnung.
 * Zuordnen: links eine Stunde wählen, dann rechts ihr Ziel anklicken.
 */
export const TimeGridMigrationMapping = ({
  migration,
  newTimeGridName,
  oldTimeGridName,
  resetAvailabilitiesLabel,
}: TimeGridMigrationMappingProps) => {
  const { t } = useTranslation();
  const { cardsPerEntry, mapping, newEntries, oldEntries, preview } = migration;
  const [selectedOldUuid, setSelectedOldUuid] = useState<string | null>(null);

  // Anderes Raster: eine Auswahl aus dem alten Raster gibt es dort nicht mehr.
  // biome-ignore lint/correctness/useExhaustiveDependencies: reset bei Rasterwechsel
  useEffect(() => {
    setSelectedOldUuid(null);
  }, [oldEntries, newEntries]);

  const sourceByTarget = new Map(Object.entries(mapping).map(([from, to]) => [to, from]));
  const newIndexByUuid = new Map(newEntries.map((entry, index) => [entry.uuid, index]));
  const pauseUuids = new Set(newEntries.filter((entry) => entry.pause).map((entry) => entry.uuid));
  const rowCount = Math.max(oldEntries.length, newEntries.length);

  const handleNewEntryClick = (newEntryUuid: string) => {
    if (!selectedOldUuid) return;
    // Erneuter Klick auf das eigene Ziel löst die Verbindung wieder.
    const isOwnTarget = mapping[selectedOldUuid] === newEntryUuid;
    migration.setTarget(selectedOldUuid, isOwnTarget ? NO_TARGET : newEntryUuid);
    setSelectedOldUuid(null);
  };

  return (
    <div
      className={styles['time-grid-migration']}
      style={{ '--row-height': `${ROW_HEIGHT}px`, '--row-gap': `${ROW_GAP}px` } as CSSProperties}
    >
      <p className={styles['how-to']}>{t('timeGrid.migration.howTo')}</p>

      <div className={styles.board}>
        <div className={styles['column-title']}>
          {t('timeGrid.migration.currentTimeGrid')}
          <span className={styles['column-name']}>{oldTimeGridName ?? '–'}</span>
        </div>
        <div />
        <div className={styles['column-title']}>
          {t('timeGrid.migration.newTimeGrid')}
          <span className={styles['column-name']}>{newTimeGridName ?? '–'}</span>
        </div>

        <div className={styles.column}>
          {oldEntries.map((entry) => {
            const cardCount = cardsPerEntry.get(entry.uuid) ?? 0;
            const hasTarget = !!mapping[entry.uuid];
            // In Pausen lassen sich keine Karten stecken: ein Ziel, das eine Pause ist, hält keine Karten.
            const losesCards = cardCount > 0 && (!hasTarget || pauseUuids.has(mapping[entry.uuid]));
            return (
              <div
                key={entry.uuid}
                className={classNames(styles.entry, {
                  [styles['is-pause']]: entry.pause,
                  [styles['is-selected']]: selectedOldUuid === entry.uuid,
                  [styles['is-unassigned']]: !hasTarget,
                  [styles['loses-cards']]: losesCards,
                })}
              >
                <button
                  type='button'
                  className={styles['entry-button']}
                  aria-pressed={selectedOldUuid === entry.uuid}
                  onClick={() => setSelectedOldUuid((prev) => (prev === entry.uuid ? null : entry.uuid))}
                >
                  <span className={styles['entry-name']}>{entry.shortName}</span>
                  <span className={styles['entry-meta']}>
                    {entry.pause && cardCount === 0
                      ? t('timeGrid.item.isPause')
                      : t('timeGrid.migration.cardCount', { count: cardCount })}
                  </span>
                  <span className={styles['entry-time']}>{formatTime(entry)}</span>
                </button>
                {hasTarget && (
                  <button
                    type='button'
                    className={styles.remove}
                    aria-label={t('timeGrid.migration.removeMapping')}
                    title={t('timeGrid.migration.removeMapping')}
                    onClick={() => {
                      migration.setTarget(entry.uuid, NO_TARGET);
                      setSelectedOldUuid(null);
                    }}
                  >
                    <CloseIcon className='small' />
                  </button>
                )}
              </div>
            );
          })}
        </div>

        <svg
          className={styles.links}
          height={Math.max(rowCount * (ROW_HEIGHT + ROW_GAP) - ROW_GAP, 0)}
          aria-hidden='true'
        >
          {oldEntries.map((entry, oldIndex) => {
            const newIndex = newIndexByUuid.get(mapping[entry.uuid]);
            if (newIndex === undefined) return null;
            return (
              <line
                key={entry.uuid}
                className={classNames(styles.link, { [styles['is-selected']]: selectedOldUuid === entry.uuid })}
                x1='0'
                x2='100%'
                y1={rowCenter(oldIndex)}
                y2={rowCenter(newIndex)}
              />
            );
          })}
        </svg>

        <div className={styles.column}>
          {newEntries.map((entry) => {
            const sourceUuid = sourceByTarget.get(entry.uuid);
            // Eine neue Stunde kann nur Ziel EINER alten Stunde sein: belegte Stunden sind für andere gesperrt.
            const isTakenByOther = !!sourceUuid && sourceUuid !== selectedOldUuid;
            return (
              <div
                key={entry.uuid}
                className={classNames(styles.entry, {
                  [styles['is-pause']]: entry.pause,
                  [styles['is-selected']]: !!selectedOldUuid && sourceUuid === selectedOldUuid,
                  [styles['is-unassigned']]: !sourceUuid,
                  [styles['is-available']]: !!selectedOldUuid && !isTakenByOther,
                })}
              >
                <button
                  type='button'
                  className={styles['entry-button']}
                  disabled={!selectedOldUuid || isTakenByOther}
                  onClick={() => handleNewEntryClick(entry.uuid)}
                >
                  <span className={styles['entry-name']}>{entry.shortName}</span>
                  <span className={styles['entry-meta']}>{entry.pause ? t('timeGrid.item.isPause') : ''}</span>
                  <span className={styles['entry-time']}>{formatTime(entry)}</span>
                </button>
              </div>
            );
          })}
        </div>
      </div>

      {newEntries.length > 0 && (
        <>
          <p className={styles.summary}>
            {t('timeGrid.migration.summary', { migrated: preview.migrated, unplaced: preview.unplaced })}
          </p>
          <Checkbox
            className='mt-4'
            label={resetAvailabilitiesLabel}
            name='resetAvailabilities'
            checked={migration.resetAvailabilities}
            onChange={(e) => migration.setResetAvailabilities(e.target.checked)}
          />
        </>
      )}
    </div>
  );
};
